import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useDispatch } from "react-redux";
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey, faCopy } from "@fortawesome/free-solid-svg-icons";
import { customersOperations } from "state/ducks/store/customers";

/**
 * Client qui n'arrive pas à réinitialiser son mot de passe (mail en spam, adresse
 * de compte différente…) : l'agent génère le lien « nouveau mot de passe » et le
 * copie pour le transmettre lui-même (réponse Freshdesk…). Il ne voit jamais le mot de passe, que le client choisit.
 * Lien valable 24 h, utilisable une fois ; un nouveau lien remplace le précédent.
 */
const PopupPasswordReset = ({ show, onHide, customer }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const linkInput = useRef(null);
  const customerId = customer?.id;

  // Rien ne doit survivre d'un client à l'autre.
  useEffect(() => {
    if (show) {
      setResult(null);
      setError(null);
      setCopied(false);
      setCopyFailed(false);
    }
  }, [show, customerId]);

  const generate = async () => {
    setLoading(true);
    setError(null);
    setCopied(false);
    try {
      const res = await dispatch(customersOperations.createPasswordResetLink(customerId));
      setResult(res?.data?.result || null);
    } catch (err) {
      setError(
        err?.response?.data?.result?.message ||
        err?.response?.data?.message ||
        "Le lien n'a pas pu être généré."
      );
    }
    setLoading(false);
  };

  // moulin-admin tourne dans une iframe du BO Symfony, sans permission
  // « clipboard-write » (et en http en local) : navigator.clipboard y est absent
  // ou refusé. Repli sur la copie de la sélection du champ, qui passe partout.
  const copy = async () => {
    setCopyFailed(false);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(result.url);
        setCopied(true);
        return;
      }
    } catch (e) {
      // on tente le repli
    }
    const input = linkInput.current;
    if (input) {
      input.focus();
      input.select();
      input.setSelectionRange(0, result.url.length);
      try {
        if (document.execCommand("copy")) {
          setCopied(true);
          return;
        }
      } catch (e) {
        // copie impossible
      }
    }
    setCopied(false);
    setCopyFailed(true);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faKey} /> Nouveau mot de passe
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Pour un client qui n'arrive pas à réinitialiser son mot de passe : vous lui transmettez un lien
          pour <b>choisir lui-même</b> son nouveau mot de passe : vous ne le voyez jamais.
        </p>
        <Rules>
          <li>Lien valable <b>24 heures</b>, utilisable une seule fois.</li>
          <li>Générer un nouveau lien annule le précédent.</li>
        </Rules>

        <Button variant="primary" onClick={generate} disabled={loading}>
          <FontAwesomeIcon icon={faKey} /> {loading ? "Génération…" : "Générer le lien"}
        </Button>
        <Hint>
          Copiez-le ensuite dans votre réponse au client (Freshdesk…).
        </Hint>

        {error && <Error>{error}</Error>}

        {result && (
          <Result>
            <p className="mb-2">
              Lien généré, valable jusqu'au{" "}
              <b>{moment(result.expiresAt).format("DD/MM à HH:mm")}</b>.
            </p>
            <InputGroup>
              <Form.Control ref={linkInput} value={result.url} readOnly onFocus={(e) => e.target.select()} />
              <InputGroup.Append>
                <Button variant={copied ? "success" : "outline-secondary"} onClick={copy}>
                  <FontAwesomeIcon icon={faCopy} /> {copied ? "Copié" : "Copier"}
                </Button>
              </InputGroup.Append>
            </InputGroup>
            {copyFailed && (
              <small className="text-danger">
                Copie automatique impossible : le lien est sélectionné, faites Ctrl+C (ou Cmd+C).
              </small>
            )}
          </Result>
        )}
      </Modal.Body>
    </Modal>
  );
};

const Rules = styled.ul`
  padding-left: 1.2rem;
  color: #555;
`;

const Hint = styled.p`
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: #777;
`;

const Error = styled.div`
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: #fdecea;
  color: #a94442;
  border-radius: 4px;
`;

const Result = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  background: #eef7ef;
  border-radius: 4px;
`;

export default PopupPasswordReset;
